import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import AnimatedMetric from "./AnimatedMetric";
import DemoBadge from "./DemoBadge";
import { DollarSign, Coins, TrafficCone, Search } from "lucide-react";

const TILES = [
  { key: "total_budget_usd",      label: "Total Budget",          Icon: DollarSign,  tone: "success" },
  { key: "reserves_usd",          label: "Reserves",              Icon: Coins,       tone: "warning" },
  { key: "capital_plan_usd",      label: "Capital Plan",          Icon: TrafficCone, tone: "critical" },
  { key: "redeployment_low_usd",  label: "Redeployment Potential",Icon: Search,      tone: "electric", range: true },
];

export const FiscalOverviewPanel = ({ entity = "Manatee County" }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/engine/fiscal-overview", { params: { entity_name: entity } })
      .then(r => setD(r.data)).catch(() => {});
  }, [entity]);

  if (!d) return <div className="glass p-6 animate-pulse"><div className="h-56"/></div>;

  const pressure = Math.round((d.fiscal_pressure || 0) * 100);

  return (
    <div className="glass p-0 overflow-hidden" data-testid="fiscal-overview">
      <div className="flex items-center justify-between p-5 border-b border-gold-dim/20">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-electric">Fiscal Transparency Model</div>
          <h3 className="font-display text-2xl mt-1">{d.entity_name} — Budget Risk Overview</h3>
        </div>
        <DemoBadge />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
        {TILES.map(({ key, label, Icon, tone, range }) => (
          <div key={key} className="p-5 border-r border-gold-dim/10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
              background: tone === "success" ? "radial-gradient(circle at top left, #2F855A, transparent 60%)" :
                          tone === "warning" ? "radial-gradient(circle at top left, #ECC94B, transparent 60%)" :
                          tone === "critical" ? "radial-gradient(circle at top left, #E53E3E, transparent 60%)" :
                          "radial-gradient(circle at top left, #2D9CDB, transparent 60%)"
            }}/>
            <div className="relative">
              <div className="flex items-center gap-2 text-steel">
                <Icon size={14} /><span className="text-[10px] uppercase tracking-[0.22em]">{label}</span>
              </div>
              <div className="metric text-2xl md:text-3xl text-cream mt-2">
                {range
                  ? <><AnimatedMetric value={d.redeployment_low_usd/1e6} prefix="$" decimals={0} suffix="M" /> <span className="text-xl text-steel mx-1">–</span> <AnimatedMetric value={d.redeployment_high_usd/1e6} prefix="$" decimals={0} suffix="M" /></>
                  : <AnimatedMetric value={d[key] >= 1e9 ? d[key]/1e9 : d[key]/1e6} prefix="$" decimals={d[key] >= 1e9 ? 2 : 0} suffix={d[key] >= 1e9 ? "B" : "M"} />
                }
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-5 border-t border-gold-dim/15">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-steel">
          <span>Fiscal Pressure Level</span>
          <span>{pressure}%</span>
        </div>
        <div className="mt-2 h-2 rounded-full overflow-hidden bg-navy-deep">
          <div style={{
            width: `${pressure}%`,
            background: "linear-gradient(90deg, #2F855A 0%, #ECC94B 50%, #E53E3E 100%)"
          }} className="h-full transition-all duration-700" />
        </div>
        <div className="mt-4 text-sm italic text-cream-dim border-l-2 border-gold pl-3">
          "{d.quote}"
        </div>
      </div>
    </div>
  );
};

export default FiscalOverviewPanel;
